'use client'

import { useMemo, useState } from 'react'
import cn from 'classnames'
import { useTranslations } from 'next-intl'

import shared from '@booking/index.module.scss'
import { Modal } from '@booking/components/Modal'
import { useBookingState } from '@booking/state/booking.context'
import { setReferrer } from '@booking/services/api'

import styles from './index.module.scss'
import { useBookingExternal } from '../../state/external.context'

type AttributionValue = {
  slug: string
  label: string
}

type AttributionItem = {
  key: string
  text: string
}

type AttributionModalProps = {
  values?: AttributionValue[] | false
}

export default function AttributionModal({ values = false }: AttributionModalProps) {
  const t = useTranslations('booking')
  const { cart } = useBookingState()
  const { externalData } = useBookingExternal()

  const [modalIsOpen, setModalIsOpen] = useState(true)
  const [error, setError] = useState(false)
  const [other, setOther] = useState('')
  const [selected, setSelected] = useState<AttributionItem[]>([])

  const items = useMemo<AttributionItem[]>(() => {
    if (values && Array.isArray(values)) {
      return [
        ...values.map((item) => ({
          key: item.slug,
          text: item.label,
        })),
        { key: 'other', text: t('referral.options.other') },
      ]
    }

    if (externalData.attributionValues) {
      return [
        ...externalData.attributionValues.map((item) => ({
          key: item.key,
          text: item.text,
        })),
        { key: 'other', text: t('referral.options.other') },
      ]
    }

    return [
      { key: 'facebook_instagram', text: t('referral.options.facebook_instagram') },
      { key: 'discover_belem', text: t('referral.options.discover_belem') },
      { key: 'google_youtube', text: t('referral.options.google_youtube') },
      { key: 'tiktok', text: t('referral.options.tiktok') },
      { key: 'airport', text: t('referral.options.airport') },
      { key: 'tiquets', text: t('referral.options.tiquets') },
      { key: 'bus', text: t('referral.options.bus') },
      { key: 'tourguide', text: t('referral.options.tourguide') },
      { key: 'gyg', text: t('referral.options.gyg') },
      { key: 'news', text: t('referral.options.news') },
      { key: 'wom', text: t('referral.options.wom') },
      { key: 'other', text: t('referral.options.other') },
    ]
  }, [t, values, externalData.attributionValues])

  const isOtherSelected = selected.some((item) => item.key === 'other')

  const toggleSelected = (item: AttributionItem) => {
    setError(false)

    setSelected((prev) => {
      const exists = prev.some((selectedItem) => selectedItem.key === item.key)

      if (exists) {
        return prev.filter((selectedItem) => selectedItem.key !== item.key)
      }

      return [...prev, item]
    })
  }

  const onSubmit = async () => {
    const selectedKeys = selected.map((item) => item.key)
    const requiresOtherText = selectedKeys.includes('other') && !other.trim()

    if (!selectedKeys.length || requiresOtherText) {
      setError(true)
      return
    }

    if (!cart?.id) {
      setError(true)
      return
    }

    try {
      await setReferrer(cart.id, selectedKeys, other.trim())
      setModalIsOpen(false)
    } catch {
      setError(true)
    }
  }

  return (
    <Modal
      modalIsOpen={modalIsOpen}
      setModalIsOpen={setModalIsOpen}
      className={cn(styles.modal)}
      closeIconClassName={styles.close}
    >
      <div className={styles.inner}>
        <div className={styles.header}>
          <h3 className={styles.title}>{t('referral.modalTitle')}</h3>
          <div
            className={styles.desc}
            dangerouslySetInnerHTML={{ __html: t.raw('referral.modalDesc') }}
          />
        </div>

        <div className={styles.body}>
          <div className={styles.bodyHeader}>
            <h4 className={styles.title}>{t('referral.title')}</h4>
            <h4 className={styles.subTitle}>{t('referral.subTitle')}</h4>
          </div>

          <div className={styles.referralItems}>
            {items.map((item) => {
              const isActive = selected.some((selectedItem) => selectedItem.key === item.key)

              return (
                <button
                  key={item.key}
                  type="button"
                  className={cn(styles.item, isActive && styles.active)}
                  onClick={() => toggleSelected(item)}
                >
                  <span>{item.text}</span>
                </button>
              )
            })}
          </div>

          <div className={cn(styles.otherField, isOtherSelected && styles.active)}>
            <input
              type="text"
              className={styles.input}
              value={other}
              onChange={(e) => {
                setOther(e.target.value)
                if (error) setError(false)
              }}
              placeholder={t('referral.placeholder')}
            />
            {error && isOtherSelected && !other.trim() && (
              <div className={styles.error}>{t('referral.required')}</div>
            )}
          </div>

          {error && !selected.length && (
            <div className={styles.error}>{t('referral.required')}</div>
          )}

          <button type="button" onClick={onSubmit} className={cn(shared.button, styles.button)}>
            {t('referral.linkText')}
          </button>
        </div>
      </div>
    </Modal>
  )
}
